import {
  Box,
  Chip,
  styled,
  Dialog,
  AppBar,
  Toolbar,
  Typography,
  Slide,
  IconButton,
  CircularProgress,
  Paper,
  Grid,
  Pagination,
  Stack,
} from "@mui/material";
import { TransitionProps } from "@mui/material/transitions";

import FlexBox from "components/FlexBox";
import useTitle from "hooks/useTitle";
import React, { FC, useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { listSubmissions } from "redux/forms";
import { loggedInUser } from "utils/state";
import CloseIcon from "icons/CloseIcon";
import LightIconButton from "components/LightIconButton";
import PlusIcon from "icons/PlusIcon";
import ShareIcon from "icons/ShareIcon";
import moment from "moment";
import { H4, H6, Tiny } from "components/Typography";

// ─────────────────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────────────────

const CARDS_PER_PAGE = 10;

const INVOICE_CHIP: Record<string, { label: string; color: "default" | "warning" | "success" }> = {
  not_invoiced: { label: "Not invoiced", color: "default" },
  invoiced:     { label: "Invoiced",     color: "warning"  },
  paid:         { label: "Invoice paid", color: "success"  },
};

const COMM_PAID_COLOR   = "#2CC5BD";
const COMM_UNPAID_COLOR = "#FD396D";

const STATUS_COLORS: Record<string, string> = {
  new:                    "#9E9E9E",
  acknowledged:           "#2196F3",
  "customer available":   "#FF9800",
  "customer unavailable": "#FD396D",
  "vehicle available":    "#8BC34A",
  "vehicle unavailable":  "#FD396D",
  "report created":       "#A798FF",
  done:                   "#2CC5BD",
};

// ─────────────────────────────────────────────────────────────────────────────
// Styled helpers
// ─────────────────────────────────────────────────────────────────────────────

const StyledFlexBox = styled(FlexBox)(({ theme }) => ({
  justifyContent: "space-between",
  alignItems: "center",
  flexWrap: "wrap",
  marginBottom: 20,
  [theme.breakpoints.down(500)]: {
    width: "100%",
    "& .MuiButton-root": { width: "100%", marginTop: 15 },
  },
}));

const SectionLabel = styled(Typography)(({ theme }) => ({
  fontSize: 10,
  fontWeight: 700,
  letterSpacing: 1.2,
  textTransform: "uppercase",
  color: theme.palette.text.disabled,
  marginBottom: 8,
}));

const DetailCard = styled(Paper)(({ theme }) => ({
  borderRadius: 12,
  padding: "16px 20px",
  marginBottom: 12,
  backgroundColor: theme.palette.mode === "dark" ? theme.palette.background.default : "#F9FAFB",
  boxShadow: "none",
  border: `1px solid ${theme.palette.divider}`,
}));

const FieldRow: FC<{ label: string; value?: string | number | null }> = ({ label, value }) => (
  <Box mb={1.5}>
    <Typography variant="caption" sx={{ color: "text.disabled", fontWeight: 600, display: "block" }}>
      {label}
    </Typography>
    <Typography variant="body2" sx={{ fontWeight: 600, color: "text.primary" }}>
      {value ?? "—"}
    </Typography>
  </Box>
);

// ─────────────────────────────────────────────────────────────────────────────
// Watermark — shown only when commission is paid
// ─────────────────────────────────────────────────────────────────────────────

const CommissionWatermark: FC = () => (
  <Box
    aria-hidden
    sx={{
      position: "fixed",
      inset: 0,
      pointerEvents: "none",
      zIndex: 1400,
      overflow: "hidden",
      display: "grid",
      gridTemplateColumns: "repeat(3, 1fr)",
      gridTemplateRows: "repeat(10, 1fr)",
    }}
  >
    {Array.from({ length: 30 }).map((_, i) => (
      <FlexBox key={i} alignItems="center" justifyContent="center" sx={{ overflow: "hidden" }}>
        <Typography
          sx={{
            transform: "rotate(-38deg)",
            color: "rgba(44,197,189,0.13)",
            fontWeight: 900,
            fontSize: 13,
            letterSpacing: 2,
            userSelect: "none",
            whiteSpace: "nowrap",
          }}
        >
          ✓ COMMISSION PAID
        </Typography>
      </FlexBox>
    ))}
  </Box>
);

// ─────────────────────────────────────────────────────────────────────────────
// Valuation request card
// ─────────────────────────────────────────────────────────────────────────────

const ValuationCard: FC<{ row: any; onClick: () => void }> = ({ row, onClick }) => {
  const commissionExists: boolean = !!row.commission_exists;
  const commissionPaid:   boolean = !!row.commission_paid;

  const accentColor = commissionPaid   ? COMM_PAID_COLOR
                    : commissionExists ? "#FF9800"
                    :                   COMM_UNPAID_COLOR;
  const bgColor     = commissionPaid   ? "rgba(44,197,189,0.06)"
                    : commissionExists ? "rgba(255,152,0,0.05)"
                    :                   "rgba(253,57,109,0.05)";

  const invoiceStatus = row.invoice_status ?? "not_invoiced";
  const invoiceChip   = INVOICE_CHIP[invoiceStatus] ?? INVOICE_CHIP.not_invoiced;
  const statusColor   = STATUS_COLORS[row.status] ?? "#9E9E9E";

  return (
    <Paper
      onClick={onClick}
      elevation={0}
      sx={{
        borderRadius: 3,
        border: "1px solid",
        borderColor: "divider",
        borderLeft: `4px solid ${accentColor}`,
        backgroundColor: bgColor,
        p: 2,
        mb: 1.5,
        cursor: "pointer",
        transition: "box-shadow 0.15s ease, transform 0.1s ease",
        "&:hover": { boxShadow: "0 4px 20px rgba(0,0,0,0.08)", transform: "translateY(-1px)" },
        "&:active": { transform: "translateY(0)" },
      }}
    >
      {/* Top row */}
      <FlexBox justifyContent="space-between" alignItems="flex-start" mb={1}>
        <Box>
          <H6 color="text.primary" sx={{ fontWeight: 700 }}>{row.client_name}</H6>
          <Tiny color="text.disabled">{row.client_phone}</Tiny>
        </Box>
        <Box
          sx={{
            borderRadius: 10,
            px: 1.5,
            py: 0.25,
            backgroundColor: "#A798FF",
            color: "#fff",
            fontSize: 11,
            fontWeight: 700,
            whiteSpace: "nowrap",
            ml: 1,
          }}
        >
          {row.reg_no}
        </Box>
      </FlexBox>

      {/* Partner + date */}
      <FlexBox justifyContent="space-between" alignItems="center" mb={1.5}>
        <Tiny color="text.primary" sx={{ fontWeight: 600 }}>
          {row.partner?.names ?? "—"}
        </Tiny>
        <Tiny color="text.disabled">
          {row.created_on ? moment(row.created_on).format("DD MMM YYYY") : "—"}
        </Tiny>
      </FlexBox>

      {/* Billed amount if available */}
      {row.invoice_grand_total && (
        <FlexBox gap={2} mb={1.5}>
          <Box>
            <Tiny color="text.disabled" sx={{ fontWeight: 600 }}>BILLED</Tiny>
            <Tiny color="text.primary"  sx={{ fontWeight: 700 }}>KShs {row.invoice_grand_total}</Tiny>
          </Box>
        </FlexBox>
      )}

      {/* Chips */}
      <FlexBox gap={1} flexWrap="wrap">
        <Chip size="small" label={invoiceChip.label} color={invoiceChip.color} sx={{ fontSize: 10, height: 20 }} />
        <Chip
          size="small"
          label={
            commissionPaid   ? "✓ Commission paid" :
            commissionExists ? "Comm. awaiting payment" :
                               "Commission pending"
          }
          sx={{
            fontSize: 10,
            height: 20,
            fontWeight: 600,
            backgroundColor: commissionPaid
              ? "rgba(44,197,189,0.18)"
              : commissionExists
                ? "rgba(255,152,0,0.18)"
                : "rgba(253,57,109,0.15)",
            color: commissionPaid ? "#1a8c86" : commissionExists ? "#b36a00" : "#c0224a",
          }}
        />
        <Chip
          size="small"
          label={row.status ?? "—"}
          sx={{
            fontSize: 10,
            height: 20,
            fontWeight: 600,
            backgroundColor: statusColor + "22",
            color: statusColor,
            textTransform: "capitalize",
          }}
        />
      </FlexBox>
    </Paper>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Transition — defined outside the component for a stable reference.
// ─────────────────────────────────────────────────────────────────────────────

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & { children: React.ReactElement },
  ref: React.Ref<unknown>
) { return <Slide direction="up" ref={ref} {...props} />; });

// ─────────────────────────────────────────────────────────────────────────────
// Main page
// ─────────────────────────────────────────────────────────────────────────────

const ListFormsFOValuationRequests: FC = () => {
  useTitle("Valuation requests");

  const user     = loggedInUser();
  const navigate = useNavigate();

  const [state,      setState]      = useState("loading");
  const [open,       setOpen]       = useState(false);
  const [curViewRow, setCurViewRow] = useState<any>(null);
  const [rows,       setRows]       = useState<any[]>([]);
  const [page,       setPage]       = useState(1);

  const pagedRows = useMemo(() => {
    const start = (page - 1) * CARDS_PER_PAGE;
    return rows.slice(start, start + CARDS_PER_PAGE);
  }, [rows, page]);

  const pageCount = Math.ceil(rows.length / CARDS_PER_PAGE);

  const handleRowClicked  = (row: any) => { setOpen(true); setCurViewRow(row); };
  const handleCloseDrawer = ()         => { setOpen(false); setCurViewRow(null); };

  useEffect(() => {
    let cancelled = false;
    setState("loading");
    listSubmissions({ params: { created_by: user?.id }, url: `v1/valuation/request-list/` })
      .then((response: any) => {
        if (cancelled) return;
        if (response?.status === 200) setRows(response.data.results);
        else toast.error("Something went wrong.");
        setState("loaded");
      })
      .catch((error) => {
        if (cancelled) return;
        toast.error(error);
        setState("loaded");
      });
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Share ────────────────────────────────────────────────────────
  const buildShareText = (obj: any) => {
    if (!obj) return "";
    const commLine = obj.commission_paid
      ? "✓ Commission has been paid"
      : obj.commission_exists
        ? "Commission registered — awaiting payment"
        : "⚠ Commission NOT yet registered";
    return [
      `*Valuation request — ${obj.created_by}*`,
      `Date: ${obj.created_on ? moment(obj.created_on).format("DD MMM YYYY HH:mm") : "None"}`,
      "",
      `*Customer*`,
      `${obj.client_name}  ${obj.client_phone}`,
      `Reg: ${obj.reg_no}`,
      "",
      `Partner: ${obj.partner?.names ?? ""}`,
      commLine,
      "",
      `Status: ${obj.status}`,
      obj.remarks ? `Remarks: ${obj.remarks}` : "",
      "",
      `*Posted by ${user?.first_name} ${user?.last_name}*`,
    ].filter(Boolean).join("\n");
  };

  const handleShare = async () => {
    const message = buildShareText(curViewRow);
    if (navigator.share) {
      try {
        await navigator.share({ title: `Valuation request`, text: message });
        toast.success("Shared successfully.");
      } catch { toast.error("Share cancelled."); }
    } else {
      window.open(`https://web.whatsapp.com/send?text=${encodeURIComponent(message)}`);
    }
  };

  // ── Dialog ───────────────────────────────────────────────────────
  const commissionExists: boolean = !!curViewRow?.commission_exists;
  const commissionPaid:   boolean = !!curViewRow?.commission_paid;
  const invoiceStatus  = curViewRow?.invoice_status ?? "not_invoiced";
  const invoiceChip    = INVOICE_CHIP[invoiceStatus] ?? INVOICE_CHIP.not_invoiced;

  return (
    <Box pt={2} pb={4}>
      <StyledFlexBox>
        <LightIconButton
          onClick={() => navigate("/dashboard/forms/fo/valuation/request/new")}
          size="small"
        >
          <PlusIcon fontSize="small" />
          Create
        </LightIconButton>
        {rows.length > 0 && (
          <Tiny color="text.disabled">{rows.length} request{rows.length !== 1 ? "s" : ""}</Tiny>
        )}
      </StyledFlexBox>

      {state === "loading" ? (
        <FlexBox justifyContent="center" mt={6}>
          <CircularProgress />
        </FlexBox>
      ) : rows.length === 0 ? (
        <H4 color="text.disabled" sx={{ textAlign: "center", mt: 6 }}>No requests yet</H4>
      ) : (
        <>
          {pagedRows.map((row: any) => (
            <ValuationCard key={row.id ?? row.code} row={row} onClick={() => handleRowClicked(row)} />
          ))}
          {pageCount > 1 && (
            <Stack alignItems="flex-end" mt={2}>
              <Pagination count={pageCount} page={page} shape="rounded" onChange={(_, p) => setPage(p)} />
            </Stack>
          )}
        </>
      )}

      {/* ── Detail dialog ── */}
      <Dialog
        open={open}
        onClose={handleCloseDrawer}
        TransitionComponent={Transition}
        fullScreen
        PaperProps={{ sx: { backgroundColor: "background.default" } }}
      >
        {commissionPaid && <CommissionWatermark />}

        <AppBar
          sx={{ position: "sticky", top: 0, backgroundColor: "background.paper", boxShadow: 1, zIndex: 1500 }}
          elevation={0}
        >
          <Toolbar sx={{ justifyContent: "space-between" }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "text.primary" }}>
              Valuation request details
            </Typography>
            <FlexBox gap={1}>
              <IconButton size="small" onClick={handleShare}><ShareIcon /></IconButton>
              <IconButton size="small" onClick={handleCloseDrawer}><CloseIcon /></IconButton>
            </FlexBox>
          </Toolbar>
        </AppBar>

        <Box sx={{ p: 2, maxWidth: 600, mx: "auto", width: "100%", position: "relative", zIndex: 1 }}>

          {/* Payment status banner */}
          <Box
            sx={{
              borderRadius: 3,
              p: 2,
              mb: 2,
              background: commissionPaid
                ? "linear-gradient(135deg, rgba(44,197,189,0.18) 0%, rgba(44,197,189,0.06) 100%)"
                : commissionExists
                  ? "linear-gradient(135deg, rgba(255,152,0,0.14) 0%, rgba(255,152,0,0.04) 100%)"
                  : "linear-gradient(135deg, rgba(253,57,109,0.14) 0%, rgba(253,57,109,0.04) 100%)",
              border: `1px solid ${
                commissionPaid   ? "rgba(44,197,189,0.4)"
                : commissionExists ? "rgba(255,152,0,0.35)"
                : "rgba(253,57,109,0.35)"
              }`,
            }}
          >
            <SectionLabel sx={{ mb: 1.5 }}>Payment status</SectionLabel>
            <FlexBox gap={1} flexWrap="wrap" mb={1}>
              <Chip size="small" label={invoiceChip.label} color={invoiceChip.color} sx={{ fontWeight: 600, fontSize: 11 }} />
              {curViewRow?.invoice_no && (
                <Chip size="small" variant="outlined" label={curViewRow.invoice_no} sx={{ fontSize: 11 }} />
              )}
              {curViewRow?.invoice_grand_total && (
                <Chip size="small" variant="outlined" label={`KShs ${curViewRow.invoice_grand_total}`} sx={{ fontSize: 11 }} />
              )}
            </FlexBox>
            <FlexBox gap={1} flexWrap="wrap" alignItems="center">
              <Chip
                size="small"
                label={
                  commissionPaid   ? "✓ Commission paid" :
                  commissionExists ? "Commission registered — awaiting payment" :
                                     "⚠ Commission pending"
                }
                sx={{
                  fontWeight: 700,
                  fontSize: 11,
                  backgroundColor: commissionPaid
                    ? "rgba(44,197,189,0.22)"
                    : commissionExists
                      ? "rgba(255,152,0,0.18)"
                      : "rgba(253,57,109,0.18)",
                  color: commissionPaid ? "#0e7370" : commissionExists ? "#b36a00" : "#c0224a",
                }}
              />
              {!commissionExists && (
                <Typography variant="caption" sx={{ color: "error.main", fontWeight: 600 }}>
                  Not yet registered
                </Typography>
              )}
            </FlexBox>
          </Box>

          {/* Customer */}
          <DetailCard>
            <SectionLabel>Customer</SectionLabel>
            <Grid container spacing={1}>
              <Grid item xs={6}><FieldRow label="Name"      value={curViewRow?.client_name} /></Grid>
              <Grid item xs={6}><FieldRow label="Phone"     value={curViewRow?.client_phone} /></Grid>
              <Grid item xs={6}><FieldRow label="Policy no" value={curViewRow?.policy_no} /></Grid>
            </Grid>
          </DetailCard>

          {/* Vehicle */}
          <DetailCard>
            <SectionLabel>Vehicle</SectionLabel>
            <Grid container spacing={1}>
              <Grid item xs={6}><FieldRow label="Reg no" value={curViewRow?.reg_no} /></Grid>
              <Grid item xs={6}><FieldRow label="Make"   value={curViewRow?.make?.name ?? curViewRow?.make} /></Grid>
              <Grid item xs={6}><FieldRow label="Model"  value={curViewRow?.model?.name ?? curViewRow?.model} /></Grid>
              <Grid item xs={6}><FieldRow label="Type"   value={curViewRow?.type?.name ?? curViewRow?.type} /></Grid>
            </Grid>
          </DetailCard>

          {/* Partner */}
          <DetailCard>
            <SectionLabel>Partner</SectionLabel>
            <Grid container spacing={1}>
              <Grid item xs={6}><FieldRow label="Partner" value={curViewRow?.partner?.names} /></Grid>
              <Grid item xs={6}><FieldRow label="Broker"  value={curViewRow?.partner_broker?.name} /></Grid>
              <Grid item xs={6}>
                <FieldRow label="Purpose" value={curViewRow?.purpose === 1 ? "Financing" : curViewRow?.purpose === 4 ? "Debt Recovery" : "—"} />
              </Grid>
              <Grid item xs={6}><FieldRow label="Status" value={curViewRow?.status} /></Grid>
            </Grid>
          </DetailCard>

          {/* Inspection */}
          {(curViewRow?.inspection_date || curViewRow?.remarks) && (
            <DetailCard>
              <SectionLabel>Inspection</SectionLabel>
              <Grid container spacing={1}>
                {curViewRow?.inspection_date && (
                  <Grid item xs={12}>
                    <FieldRow label="Inspection date" value={moment(curViewRow.inspection_date).format("DD MMM YYYY")} />
                  </Grid>
                )}
                {curViewRow?.remarks && (
                  <Grid item xs={12}>
                    <FieldRow label="Remarks" value={curViewRow.remarks} />
                  </Grid>
                )}
              </Grid>
            </DetailCard>
          )}

          {/* Meta */}
          <Box sx={{ px: 1, pb: 4 }}>
            <Typography variant="caption" color="text.disabled">
              Submitted by {curViewRow?.created_by} ·{" "}
              {curViewRow?.created_on ? moment(curViewRow.created_on).format("DD MMM YYYY, HH:mm") : ""}
            </Typography>
          </Box>
        </Box>
      </Dialog>
    </Box>
  );
};

export default ListFormsFOValuationRequests;
